import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from "../base";
import { CreateObject } from "../item";
import { Place } from './place';

import { RasppiView, RasppiViewProxy } from '../views/rasppi';

import type { PosDevice } from './device';
import type { Till } from './till';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { PlaceType } from '../types';
import { RasppiConnectType } from '../types';
import { PrintDeviceType } from '../types';
import { ScaleDeviceType } from '../types';
import { CodescanDeviceType } from '../types';
import { PinpadType } from '../types';
import { TillType } from '../types';

const _schema = {
    "name": "RASPPI",
    "fields": [
        { name: "place", type: "objid" },
        { name: "status", type: "string", default: "AC" },
        { name: "id", type: "string" },
        { name: "ping", type: "date" },
        { name: "version", type: "string" }
    ],
    relate: [
        { direction: ">", target: "PLACE", by: "services", name: "place", reason: "place", child: false, class: PlaceType },
        { direction: "<", target: "RASPPICONNECT", by: "rasppi", name: "connect", reason: "connects", child: true, class: RasppiConnectType },
        { direction: "<", target: "TILL", by: "rasppi", name: "till", reason: "tills", child: true, class: TillType },
        { direction: "<", target: "PRINTER", by: "rasppi", name: "printer", reason: "printers", child: true, class: PrintDeviceType },
        { direction: "<", target: "SCALE", by: "rasppi", name: "scale", reason: "scales", child: true, class: ScaleDeviceType },
        { direction: "<", target: "CODESCAN", by: "rasppi", name: "codescan", reason: "codescans", child: true, class: CodescanDeviceType },
        { direction: "<", target: "PINPAD", by: "rasppi", name: "pinpad", reason: "pinpads", child: true, class: PinpadType },
    ]
} as const;

const _RasppiClass = CreateObject(_schema);
if (!_RasppiClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class Rasppi extends _RasppiClass {
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): Rasppi {
        return this._Copy(store) as Rasppi;
    }

    /** Changelog rows lack parent 1:N lists; register on link (see Preselect / TicketProduct). */
    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf || !this.Commited){
            return;
        }

        if (relation === 'services' && related instanceof Place){
            if (related === this.place){
                related.AddService(this);
            }
        }
    }

    override get viewProxy(): RasppiView {
        return RasppiViewProxy(this, this.data);
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get IsValid(): boolean {
        return this.status == 'AC';
    }

    /** POS terminals (PCs) connected to this service through active RASPPICONNECT (hassvc). */
    get terminals(): PosDevice[] {
        const _terminals: PosDevice[] = [];
        const _seen = new Set<PosDevice>();

        for (const _connect of this.connects ?? []){
            if (!_connect.IsValid || !_connect.hassvc || _connect.rasppi !== this){
                continue;
            }

            const _device = _connect.device;
            if (!_device?.IsValid || _seen.has(_device)){
                continue;
            }

            _seen.add(_device);
            _terminals.push(_device);
        }

        return _terminals;
    }

    /** Whether at least one POS terminal has an active service connect (`hassvc`). */
    get IsConnected(): boolean {
        return (this.terminals?.length ?? 0) > 0;
    }

    /** First operational till linked to this service (`TILL.rasppi`). */
    get till(): Till | null {
        for (const _till of this.tills || []){
            if (_till.IsValid){
                return _till;
            }
        }

        return null;
    }
}
